import type {
  CLOUD_PROVIDER,
  CloudProviderRegionMap,
  InstanceSize,
  InstanceSummary,
  Project,
  TierConfigs,
} from '@nx/state';
import { PROJECT_PROFILE, TIER } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';

import {
  dseDefaults,
  enterpriseDefaults,
  freeDefaults,
  gdsDefaults,
  isSizeAvailableForVersion,
  isSizeAvailableInRegion,
  mteDefaults,
  proDefaults,
} from '../entities/helpers';

export const FREE_MEMORY = '1GB';
export const FREE_STORAGE = '2GB';

export const getVisibleSizes = (
  sizes: InstanceSize[],
  {
    cloudProvider,
    region,
    version,
    isTrial = false,
  }: { cloudProvider?: CLOUD_PROVIDER; region?: string; version?: string; isTrial?: boolean },
) => {
  return sizes.filter((size) => {
    if (size.deprecated) {
      return false;
    }

    if (isNotNullish(cloudProvider) && size.cloudProvider !== cloudProvider) {
      return false;
    }

    if (isNotNullish(region) && !isSizeAvailableInRegion(size, region)) {
      return false;
    }

    if (isNotNullish(version) && !isSizeAvailableForVersion(size, version)) {
      return false;
    }

    if (size.isTrial !== isTrial) {
      return false;
    }

    return true;
  });
};

export const getValidRegion = (
  cloudProviderRegions: CloudProviderRegionMap,
  cloudProvider: CLOUD_PROVIDER,
  currentRegion?: string,
): string | undefined => {
  const cspRegions = cloudProviderRegions[cloudProvider];
  if (isNullish(cspRegions)) {
    return undefined;
  }

  if (isNullish(currentRegion)) {
    return cspRegions[0]?.name;
  }

  const availableRegions = cspRegions.map((region) => region.name);
  if (availableRegions.includes(currentRegion)) {
    return currentRegion;
  }

  return cspRegions[0]?.name;
};

export const defaults = (tier: TIER, tierConfigs: TierConfigs, project: Project, instances: InstanceSummary[] = []) => {
  switch (tier) {
    case TIER.FREE:
      if (isNullish(tierConfigs.free)) {
        throw new Error('Tier not supported');
      }
      return freeDefaults(tierConfigs.free, instances);
    case TIER.PROFESSIONAL:
      if (isNullish(tierConfigs.professional)) {
        throw new Error('Tier not supported');
      }
      return proDefaults(
        tierConfigs.professional,
        instances,
        project,
        project.availableActions.create_pro_trial.enabled && project.profile === PROJECT_PROFILE.EXTERNAL,
      );
    case TIER.ENTERPRISE:
      if (isNullish(tierConfigs.enterprise)) {
        throw new Error('Tier not supported');
      }
      return enterpriseDefaults(tierConfigs.enterprise, project);
    case TIER.MTE:
      if (isNullish(tierConfigs.mte)) {
        throw new Error('Tier not supported');
      }
      return mteDefaults(tierConfigs.mte, instances, project);
    case TIER.GDS:
      if (isNullish(tierConfigs.gds)) {
        throw new Error('Tier not supported');
      }
      return gdsDefaults(tierConfigs.gds, instances, project);
    case TIER.AURA_DSE:
      if (isNullish(tierConfigs.dsenterprise)) {
        throw new Error('Tier not supported');
      }
      return dseDefaults(tierConfigs.dsenterprise, project);
    default:
      throw new Error('Tier not supported');
  }
};
